/**
 * Лениво загружаемые страницы.
 *
 * Вынесены из routes.tsx: тот файл описывает конфигурацию маршрутов и
 * экспортирует данные, этот — только компоненты. Смешение ломает быстрое
 * обновление в разработке.
 *
 * Задачник и экраны входа грузятся сразу — с них начинается работа.
 * Администрирование и отчётность нужны меньшинству и не каждый день:
 * тянуть их в первый запрос значит платить за них при каждом входе.
 */

import { lazy, Suspense, type ReactNode } from "react";

export const UsersPage = lazy(() =>
  import("@/features/admin/UsersPage").then((m) => ({ default: m.UsersPage })),
);
export const UserCardPage = lazy(() =>
  import("@/features/admin/UserCardPage").then((m) => ({ default: m.UserCardPage })),
);
export const RolesPage = lazy(() =>
  import("@/features/admin/RolesPage").then((m) => ({ default: m.RolesPage })),
);
export const DictionaryPage = lazy(() =>
  import("@/features/admin/DictionaryPage").then((m) => ({ default: m.DictionaryPage })),
);
export const WorkloadNormsPage = lazy(() =>
  import("@/features/admin/WorkloadNormsPage").then((m) => ({ default: m.WorkloadNormsPage })),
);
export const DepartmentsPage = lazy(() =>
  import("@/features/admin/DepartmentsPage").then((m) => ({ default: m.DepartmentsPage })),
);
// «Мои подписки»: наблюдение за столбцами, досками, проектами и задачами.
export const SubscriptionsPage = lazy(() =>
  import("@/features/watching/SubscriptionsPage").then((m) => ({ default: m.SubscriptionsPage })),
);
export const ProfilePage = lazy(() =>
  import("@/features/profile/ProfilePage").then((m) => ({ default: m.ProfilePage })),
);
// История уведомлений открывается редко и по ссылке из колокольчика:
// сам колокольчик грузится сразу, экран истории — по требованию.
export const NotificationsPage = lazy(() =>
  import("@/features/notifications/NotificationsPage").then((m) => ({
    default: m.NotificationsPage,
  })),
);
// Заметки — отдельный кусок: редактор и его расширения нужны только здесь
// и в карточке задачи, а не при входе.
export const NotesPage = lazy(() =>
  import("@/features/notes/NotesPage").then((m) => ({ default: m.NotesPage })),
);
// Отпуска — свой кусок: годовая шкала и её диалоги нужны только здесь.
export const VacationsLayout = lazy(() =>
  import("@/features/vacations/VacationsLayout").then((m) => ({ default: m.VacationsLayout })),
);
export const VacationsOverviewPage = lazy(() =>
  import("@/features/vacations/OverviewPage").then((m) => ({ default: m.OverviewPage })),
);
export const VacationsPlannerPage = lazy(() =>
  import("@/features/vacations/PlannerPage").then((m) => ({ default: m.PlannerPage })),
);
export const VacationsProfilesPage = lazy(() =>
  import("@/features/vacations/ProfilesPage").then((m) => ({ default: m.ProfilesPage })),
);
export const VacationsSettingsPage = lazy(() =>
  import("@/features/vacations/SettingsPage").then((m) => ({ default: m.SettingsPage })),
);
export const ReportsLayout = lazy(() =>
  import("@/features/reports/ReportsLayout").then((m) => ({ default: m.ReportsLayout })),
);
export const OverviewSection = lazy(() =>
  import("@/features/reports/sections").then((m) => ({ default: m.OverviewSection })),
);
export const EmployeesSection = lazy(() =>
  import("@/features/reports/sections").then((m) => ({ default: m.EmployeesSection })),
);
export const DepartmentsSection = lazy(() =>
  import("@/features/reports/sections").then((m) => ({ default: m.DepartmentsSection })),
);
export const ProjectsSection = lazy(() =>
  import("@/features/reports/sections").then((m) => ({ default: m.ProjectsSection })),
);
export const ReportTasksSection = lazy(() =>
  import("@/features/reports/sections").then((m) => ({ default: m.ReportTasksSection })),
);
export const AttentionSection = lazy(() =>
  import("@/features/reports/sections").then((m) => ({ default: m.AttentionSection })),
);
export const EntityDetailSection = lazy(() =>
  import("@/features/reports/sections").then((m) => ({ default: m.EntityDetailSection })),
);

/** Запасной вид на время загрузки куска. Скелет, а не спиннер: форма
 *  содержимого известна заранее, и прыжка вёрстки быть не должно. */
export function Chunk({ children }: { children: ReactNode }) {
  return <Suspense fallback={<ChunkFallback />}>{children}</Suspense>;
}

function ChunkFallback() {
  return (
    <div className="flex flex-col gap-2 p-6" role="status" aria-label="Загрузка раздела">
      <div className="h-8 w-48 animate-pulse rounded-md bg-line/60" />
      <div className="h-32 w-full animate-pulse rounded-md bg-line/60" />
    </div>
  );
}

/** Описание маршрутов отдельно от роутера: тесты создают из него
 *  memory-роутер и обходят все адреса, не трогая историю браузера. */
